import { Dropdown } from '@carbon/react';
import {
  BufferedNumberInput,
  parseNumber,
  parseInteger,
} from './buffered-number-input';
import type {
  EnumOption,
  EscalationConfig,
  EscalationDirection,
  EscalationPeriod,
} from './types';
import styles from './escalation-fields.module.scss';

interface EscalationFieldsProps {
  escalation: EscalationConfig;
  enums: {
    escalationDirections: EnumOption[];
    escalationPeriods: EnumOption[];
  };
  /**
   * Lead-in phrase rendered before the field row, e.g.
   * "Base value is the starting amount." (top-level Compounded profile)
   * or "Starting from $-0.45 $/MMBtu," (custom-curve tail).
   */
  anchorText: string;
  onChange: (patch: Partial<EscalationConfig>) => void;
  /**
   * Used to namespace DOM ids so multiple instances on the same page (e.g., the
   * top-level Compounded editor AND a Custom-curve tail's escalating editor)
   * don't collide.
   */
  idPrefix: string;
}

/**
 * Prop-driven escalation editor — direction, rate %, count, period. Renders as
 * a natural-language sentence with inline controls. No form-library coupling;
 * the caller wires `onChange` to whatever state container it uses.
 * @param {EscalationFieldsProps} props - The editor props
 * @return {JSX.Element} The escalation fields editor
 */
export function EscalationFields({
  escalation,
  enums,
  anchorText,
  onChange,
  idPrefix,
}: EscalationFieldsProps): JSX.Element {
  return (
    <div className={styles.sentence}>
      <span className={styles.sentenceText}>{anchorText}</span>
      <div className={styles.directionField}>
        <Dropdown
          id={`${idPrefix}-direction`}
          label="Direction"
          titleText=""
          hideLabel
          size="sm"
          items={enums.escalationDirections}
          selectedItem={
            enums.escalationDirections.find(
              (o) => o.value === escalation.direction
            ) ?? null
          }
          itemToString={(i: EnumOption | null) => i?.label ?? ''}
          onChange={({ selectedItem }: { selectedItem: EnumOption | null }) =>
            onChange({
              direction:
                (selectedItem?.value as EscalationDirection) ?? 'ascending',
            })
          }
        />
      </div>
      <span className={styles.sentenceText}>by</span>
      <div className={styles.numField}>
        <BufferedNumberInput
          key={`${idPrefix}-pct`}
          id={`${idPrefix}-pct`}
          labelText="Percent per period"
          hideLabel
          initial={
            escalation.pctPerPeriod === ''
              ? ''
              : String(escalation.pctPerPeriod)
          }
          placeholder="0"
          onCommit={(raw) => onChange({ pctPerPeriod: parseNumber(raw) })}
        />
      </div>
      <span className={styles.sentenceText}>% every</span>
      <div className={styles.numField}>
        <BufferedNumberInput
          key={`${idPrefix}-count`}
          id={`${idPrefix}-count`}
          labelText="Periods per step"
          hideLabel
          step="1"
          initial={
            escalation.periodCount === '' ? '' : String(escalation.periodCount)
          }
          placeholder="1"
          onCommit={(raw) => onChange({ periodCount: parseInteger(raw) })}
        />
      </div>
      <div className={styles.periodField}>
        <Dropdown
          id={`${idPrefix}-period`}
          label="Period"
          titleText=""
          hideLabel
          size="sm"
          items={enums.escalationPeriods}
          selectedItem={
            enums.escalationPeriods.find(
              (o) => o.value === escalation.period
            ) ?? null
          }
          itemToString={(i: EnumOption | null) => i?.label ?? ''}
          onChange={({ selectedItem }: { selectedItem: EnumOption | null }) =>
            onChange({
              period: (selectedItem?.value as EscalationPeriod) ?? 'annually',
            })
          }
        />
      </div>
      <span className={styles.sentenceText}>, compounded.</span>
    </div>
  );
}
